import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import Header from '../components/header/Header';
import SideMenu from '../components/navigation/SideMenu';
import AppNavbar from '../components/navigation/AppNavbar';
import axiosInstance from '../authentication/AuthContext';

function normalizeDepartments(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.departments)) return data.departments;
    return [];
}

function getDepartmentName(department) {
    if (typeof department === 'string') return department;
    if (!department || typeof department !== 'object') return '';
    return (
        department.name ||
        department.department_name ||
        department.department ||
        ''
    );
}

export default function CompanyManagement() {
    const [departments, setDepartments] = useState([]);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchDepartments = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axiosInstance.get('/accounts/departments/');
            setDepartments(normalizeDepartments(response.data));
        } catch (err) {
            setError(
                err?.response?.data?.detail || 'Failed to load departments.'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
    }, []);

    const handleAddDepartment = async (event) => {
        event.preventDefault();
        const trimmedName = newDepartmentName.trim();
        if (!trimmedName) return;

        try {
            setSubmitting(true);
            setError('');
            setSuccess('');

            await axiosInstance.post('/accounts/departments/', {
                name: trimmedName,
            });

            setSuccess('Department added successfully.');
            setNewDepartmentName('');
            await fetchDepartments();
        } catch (err) {
            setError(
                err?.response?.data?.detail || 'Failed to add department.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    bgcolor: 'background.default',
                }}
            >
                <Header />
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        maxWidth: 900,
                        mx: 'auto',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <BusinessIcon />
                        Company Management
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Add New Department
                        </Typography>
                        <Box
                            component="form"
                            onSubmit={handleAddDepartment}
                            sx={{ display: 'flex', gap: 1.5 }}
                        >
                            <TextField
                                label="Department name"
                                fullWidth
                                value={newDepartmentName}
                                onChange={(event) =>
                                    setNewDepartmentName(event.target.value)
                                }
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={
                                    submitting || !newDepartmentName.trim()
                                }
                            >
                                {submitting ? 'Adding...' : 'Add'}
                            </Button>
                        </Box>
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Departments ({departments.length})
                        </Typography>
                        {loading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : departments.length === 0 ? (
                            <Typography color="text.secondary">
                                No departments found.
                            </Typography>
                        ) : (
                            <List disablePadding>
                                {departments.map((department, index) => {
                                    const label = getDepartmentName(department);
                                    const key =
                                        department?.id ??
                                        label ??
                                        `department-${index}`;
                                    return (
                                        <ListItem key={key} divider>
                                            <ListItemText
                                                primary={
                                                    label ||
                                                    'Unnamed department'
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
}
